import React, { useEffect, useState, useRef } from 'react';
import { leadActivitiesAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Linkedin, Send, Bot, RefreshCw, Clock, CheckCircle, XCircle, Loader2, Zap, ExternalLink } from 'lucide-react';
import { formatLocal } from '../lib/dateUtils';

const STATUS_STYLES = {
  completed: { icon: CheckCircle, color: 'text-emerald-500', label: 'Sent' },
  scheduled: { icon: Clock, color: 'text-blue-500', label: 'Scheduled' },
  pending: { icon: Clock, color: 'text-amber-500', label: 'Pending' },
  queued: { icon: Clock, color: 'text-blue-500', label: 'Queued' },
  failed: { icon: XCircle, color: 'text-red-500', label: 'Failed' },
  cancelled: { icon: XCircle, color: 'text-zinc-500', label: 'Cancelled' },
};

const ACTION_LABELS = {
  connect: 'Connection Request',
  message: 'Direct Message',
  follow_up: 'Follow-up',
  inmail: 'InMail',
};

function OutboundBubble({ msg }) {
  const status = STATUS_STYLES[msg.status] || STATUS_STYLES.pending;
  const StatusIcon = status.icon;
  return (
    <div className="flex justify-end mb-3" data-testid={`li-outbound-${msg.id}`}>
      <div className="max-w-[75%] space-y-1">
        <div className="bg-[#0a66c2] text-white rounded-xl rounded-tr-sm px-4 py-2.5 shadow-sm">
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.message || '(no message — connection request)'}</p>
          <div className="flex items-center justify-end gap-1.5 mt-1.5">
            {msg.auto_triggered && (
              <Zap className="h-3 w-3 text-amber-300" />
            )}
            <span className="text-[10px] opacity-60">{msg.timestamp ? formatLocal(msg.timestamp) : ''}</span>
            <StatusIcon className={`h-3 w-3 ${msg.status === 'completed' ? 'text-sky-200' : 'opacity-50'}`} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-1.5 px-1">
          {msg.action && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-blue-500/40 text-blue-400">
              {ACTION_LABELS[msg.action] || msg.action}
            </Badge>
          )}
          {msg.sequence_step && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-zinc-600 text-zinc-400">
              Step {msg.sequence_step}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function InboundBubble({ msg }) {
  const analysis = msg.brain_analysis;
  return (
    <div className="flex justify-start mb-3" data-testid={`li-inbound-${msg.id}`}>
      <div className="max-w-[75%] space-y-1">
        <div className="bg-[#1b1f23] text-zinc-100 rounded-xl rounded-tl-sm px-4 py-2.5 shadow-sm border border-zinc-700/50">
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.message}</p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-[10px] opacity-50">{msg.timestamp ? formatLocal(msg.timestamp) : ''}</span>
          </div>
        </div>
        {analysis && (
          <div className="flex items-center gap-1.5 px-1 flex-wrap">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-zinc-600 text-zinc-400">
              <Bot className="h-2.5 w-2.5 mr-0.5" /> {analysis.intent?.replace(/_/g, ' ')}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LinkedInThreadView({ leadId, leadName, linkedinUrl, onGenerateMessage }) {
  const [thread, setThread] = useState([]);
  const [stats, setStats] = useState({ outbound_count: 0, inbound_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const scrollRef = useRef(null);

  const loadThread = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await leadActivitiesAPI.getLinkedInConversation(leadId);
      const data = res.data || res;
      setThread(data.thread || []);
      setStats({ outbound_count: data.outbound_count || 0, inbound_count: data.inbound_count || 0 });
    } catch {
      setThread([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (leadId) loadThread();
  }, [leadId]);

  useEffect(() => {
    if (scrollRef.current && thread.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="linkedin-thread-view">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#0a66c2]/20 flex items-center justify-center">
            <Linkedin className="h-4 w-4 text-[#0a66c2]" />
          </div>
          <div>
            <p className="text-sm font-medium" data-testid="li-thread-lead-name">{leadName || 'LinkedIn'}</p>
            <p className="text-[11px] text-muted-foreground">
              {stats.outbound_count} sent, {stats.inbound_count} received
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {linkedinUrl && (
            <Button variant="ghost" size="sm" asChild>
              <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" data-testid="li-thread-profile-link">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
          <Button
            variant="ghost" size="sm"
            onClick={() => loadThread(true)}
            disabled={refreshing}
            data-testid="li-thread-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {onGenerateMessage && (
            <Button
              size="sm" variant="default"
              onClick={onGenerateMessage}
              className="bg-[#0a66c2] hover:bg-[#004182] text-white"
              data-testid="li-thread-generate-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Generate Message
            </Button>
          )}
        </div>
      </div>

      {/* Thread body */}
      {thread.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-[#0a66c2]/10 flex items-center justify-center mb-4">
            <Linkedin className="h-7 w-7 text-[#0a66c2]/50" />
          </div>
          <p className="text-muted-foreground text-sm">No LinkedIn outreach yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Generate a message to start the thread</p>
          {onGenerateMessage && (
            <Button size="sm" className="mt-4 bg-[#0a66c2] hover:bg-[#004182] text-white" onClick={onGenerateMessage}>
              <Send className="h-3.5 w-3.5 mr-1.5" /> Generate First Message
            </Button>
          )}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-1 max-h-[480px] bg-[#0d1117]/50"
          data-testid="li-thread-messages"
        >
          {thread.map((msg) =>
            msg.direction === 'outbound' ? (
              <OutboundBubble key={msg.id} msg={msg} />
            ) : (
              <InboundBubble key={msg.id} msg={msg} />
            )
          )}
        </div>
      )}
    </div>
  );
}
